import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Upload } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  Select,
  Textarea,
  useToast,
} from '@/components/ui'
import {
  fileToBase64,
  useContractTemplates,
  useCreateContractTemplate,
  useReplaceContractTemplate,
  useUpdateContractTemplate,
} from '@/pages/vertraege/api/templatesApi.ts'
import { useProducts } from '@/pages/vertraege/api/contractsApi.ts'
import type { ContractTemplate } from '@/pages/vertraege/types.ts'

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

export function VertragsvorlagenEditorPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()

  const editId = searchParams.get('id') ?? null

  // Load all templates to find the one being edited
  const { data: allTemplates = [], isLoading: templatesLoading, error: templatesError } = useContractTemplates()
  const template: ContractTemplate | null = useMemo(
    () => (editId ? (allTemplates.find((t) => t.id === editId) ?? null) : null),
    [allTemplates, editId],
  )

  const { data: products = [] } = useProducts({ active: true })

  const create = useCreateContractTemplate()
  const update = useUpdateContractTemplate(editId ?? '')
  const replace = useReplaceContractTemplate(editId ?? '')

  const fileInput = useRef<HTMLInputElement | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [productId, setProductId] = useState('')
  const [pendingFile, setPendingFile] = useState<{ name: string; base64: string; size: number } | null>(null)
  const [isMaterialChange, setIsMaterialChange] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // Populate form when template loads
  useEffect(() => {
    if (template) {
      setName(template.name)
      setDescription(template.description ?? '')
      setProductId(template.productId ?? '')
    } else {
      setName('')
      setDescription('')
      setProductId('')
    }
    setPendingFile(null)
    setFormError(null)
  }, [template?.id])

  const productOptions = useMemo(
    () => [
      { value: '', label: '— Optional, kein bestimmtes Produkt —' },
      ...products.map((p) => ({ value: p.id, label: p.name })),
    ],
    [products],
  )

  async function handleFile(file: File) {
    if (!file.name.toLowerCase().endsWith('.docx') && file.type !== DOCX_MIME) {
      setFormError('Bitte eine DOCX-Datei wählen (kein PDF, kein DOC).')
      return
    }
    if (file.size > 15 * 1024 * 1024) {
      setFormError('Datei zu groß (max 15 MB).')
      return
    }
    try {
      const base64 = await fileToBase64(file)
      setPendingFile({ name: file.name, base64, size: file.size })
      setFormError(null)
    } catch (err) {
      setFormError(`Datei konnte nicht gelesen werden: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  function handleSave() {
    if (!name.trim()) {
      setFormError('Name ist Pflicht.')
      return
    }
    if (!editId && !pendingFile) {
      setFormError('Bitte eine DOCX-Datei wählen.')
      return
    }

    const onError = (err: Error) => setFormError(err.message || 'Speichern fehlgeschlagen.')

    if (editId && template) {
      update.mutate(
        { name: name.trim(), description: description || null, productId: productId || null },
        {
          onSuccess: () => {
            if (pendingFile) {
              replace.mutate(
                {
                  fileContentBase64: pendingFile.base64,
                  originalFileName: pendingFile.name,
                  isMaterialChange,
                },
                {
                  onSuccess: () => {
                    toast({ title: 'Vorlage aktualisiert', tone: 'success' })
                    navigate('/stammdaten/vertragsvorlagen')
                  },
                  onError,
                },
              )
            } else {
              toast({ title: 'Vorlage aktualisiert', tone: 'success' })
              navigate('/stammdaten/vertragsvorlagen')
            }
          },
          onError,
        },
      )
    } else {
      create.mutate(
        {
          name: name.trim(),
          description: description || null,
          productId: productId || null,
          fileContentBase64: pendingFile!.base64,
          originalFileName: pendingFile!.name,
        },
        {
          onSuccess: () => {
            toast({ title: 'Vorlage angelegt', tone: 'success' })
            navigate('/stammdaten/vertragsvorlagen')
          },
          onError,
        },
      )
    }
  }

  const isPending = create.isPending || update.isPending || replace.isPending
  const isEdit = Boolean(editId)

  if (isEdit && templatesLoading) return <LoadingState />
  if (isEdit && templatesError) return <ErrorState title="Fehler" description="Vorlage konnte nicht geladen werden." />
  if (isEdit && editId && !template && !templatesLoading) {
    return <ErrorState title="Vorlage nicht gefunden" description={`Vorlage mit ID "${editId}" existiert nicht.`} />
  }

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <PageHeader
        title={isEdit ? `Vorlage bearbeiten: ${template?.name ?? '…'}` : 'Neue Vertragsvorlage'}
        description="DOCX-Datei mit Platzhaltern wie {vertragsnummer}, {kunde_name}, {beginn} hochladen."
        breadcrumbs={[
          { label: 'Stammdaten' },
          { label: 'Vorlagenbibliothek', to: '/stammdaten/vertragsvorlagen' },
          { label: isEdit ? 'Bearbeiten' : 'Neu' },
        ]}
        actions={
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate('/stammdaten/vertragsvorlagen')}
          >
            Zurück
          </Button>
        }
      />

      <Card>
        <Card.Body className="space-y-4">
          {formError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
              {formError}
            </div>
          )}

          <Input
            label="Name"
            hint='z. B. „Managed Workplace v2.4"'
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
          />

          <Textarea
            label="Beschreibung"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />

          <Select
            label="Standard-Produkt"
            hint="Optional — bei Vertragsanlage wird die Vorlage vorgeschlagen"
            value={productId}
            onValueChange={setProductId}
            options={productOptions}
          />

          <div className="space-y-2">
            <p className="text-small font-medium text-gray-700">DOCX-Datei</p>
            <div className="flex items-center gap-2">
              <input
                ref={fileInput}
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                onChange={(e) => {
                  const file = e.currentTarget.files?.[0]
                  if (file) void handleFile(file)
                  e.currentTarget.value = ''
                }}
              />
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<Upload className="w-4 h-4" />}
                onClick={() => fileInput.current?.click()}
              >
                Datei wählen
              </Button>
              {pendingFile ? (
                <Badge variant="brand" size="sm">
                  {pendingFile.name} · {(pendingFile.size / 1024).toFixed(0)} KB
                </Badge>
              ) : isEdit && template?.originalFileName ? (
                <Badge variant="neutral" size="sm">
                  aktuell: {template.originalFileName} · v{template.version}
                </Badge>
              ) : (
                <span className="text-caption text-gray-500">keine Datei gewählt</span>
              )}
            </div>
            {isEdit && pendingFile && (
              <label className="flex items-center gap-2 text-small text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isMaterialChange}
                  onChange={(e) => setIsMaterialChange(e.target.checked)}
                  className="rounded"
                />
                Wesentliche Änderung (Material Change) — wird bei Kundenliste und Diff hervorgehoben
              </label>
            )}
            <p className="text-caption text-gray-500">
              Max 15 MB · Erkannte Platzhalter werden nach dem Speichern in der Bibliothek angezeigt.
            </p>
          </div>

          {isEdit && template && (
            <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 space-y-1">
              <p className="text-caption font-medium text-gray-600">Vorhandene Platzhalter (v{template.version})</p>
              <div className="flex flex-wrap gap-1">
                {(template.placeholders as string[] ?? []).length === 0 ? (
                  <span className="text-caption text-gray-400">—</span>
                ) : (
                  (template.placeholders as string[]).map((p) => (
                    <Badge key={p} variant="neutral" size="sm">{p}</Badge>
                  ))
                )}
              </div>
            </div>
          )}
        </Card.Body>

        <Card.Footer className="flex justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/stammdaten/vertragsvorlagen')}
            disabled={isPending}
          >
            Abbrechen
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={isPending}>
            {isEdit ? 'Speichern' : 'Vorlage anlegen'}
          </Button>
        </Card.Footer>
      </Card>
    </div>
  )
}
